import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
	X,
	Paperclip,
	Loader2,
	Send,
	Image,
	Video,
	Mic,
	FileText,
} from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'

export type MediaType = 'image' | 'video' | 'audio' | 'file'

interface MediaAttachmentPickerProps {
	onMediaSelect: (file: File, mediaType: MediaType) => void
	onClose: () => void
	isUploading?: boolean
	initialFile?: File | null
}

export function getMediaTypeFromFile(file: File): MediaType {
	const mimeType = file.type

	if (mimeType.startsWith('image/')) {
		return 'image'
	}
	if (mimeType.startsWith('video/')) {
		return 'video'
	}
	if (mimeType.startsWith('audio/')) {
		return 'audio'
	}
	return 'file'
}

export function getMediaIcon(mediaType: MediaType) {
	switch (mediaType) {
		case 'image':
			return <Image className="h-4 w-4" />
		case 'video':
			return <Video className="h-4 w-4" />
		case 'audio':
			return <Mic className="h-4 w-4" />
		case 'file':
			return <FileText className="h-4 w-4" />
	}
}

export function getMediaLabel(mediaType: MediaType) {
	switch (mediaType) {
		case 'image':
			return 'รูปภาพ'
		case 'video':
			return 'วิดีโอ'
		case 'audio':
			return 'เสียง'
		case 'file':
			return 'ไฟล์'
	}
}

export function formatFileSize(bytes: number) {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function MediaAttachmentPicker({
	onMediaSelect,
	onClose,
	isUploading = false,
	initialFile = null,
}: MediaAttachmentPickerProps) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [isDragging, setIsDragging] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const initialFileProcessed = useRef(false)

	const detectedMediaType = useMemo(() => {
		if (!selectedFile) return null
		return getMediaTypeFromFile(selectedFile)
	}, [selectedFile])

	// Cleanup preview URL on unmount
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl)
			}
		}
	}, [previewUrl])

	// Process initial file when provided (e.g., from paste)
	useEffect(() => {
		if (initialFile && !initialFileProcessed.current) {
			initialFileProcessed.current = true
			// Validate file size (max 50MB for all media)
			if (initialFile.size <= 50 * 1024 * 1024) {
				setSelectedFile(initialFile)
				// Create preview URL for image/video
				const mediaType = getMediaTypeFromFile(initialFile)
				if (mediaType === 'image' || mediaType === 'video') {
					const url = URL.createObjectURL(initialFile)
					setPreviewUrl(url)
				}
			}
		}
	}, [initialFile])

	// Handle paste events when picker is open
	useEffect(() => {
		const handlePaste = (e: ClipboardEvent) => {
			const items = e.clipboardData?.items
			if (!items) return

			for (const item of items) {
				if (item.kind === 'file') {
					const file = item.getAsFile()
					if (file && file.size <= 50 * 1024 * 1024) {
						e.preventDefault()
						// Clean up previous preview URL
						if (previewUrl) {
							URL.revokeObjectURL(previewUrl)
						}
						setSelectedFile(file)
						const mediaType = getMediaTypeFromFile(file)
						if (mediaType === 'image' || mediaType === 'video') {
							setPreviewUrl(URL.createObjectURL(file))
						} else {
							setPreviewUrl(null)
						}
						return
					}
				}
			}
		}

		document.addEventListener('paste', handlePaste)
		return () => document.removeEventListener('paste', handlePaste)
	}, [previewUrl])

	const handleFileSelect = useCallback((file: File) => {
		// Validate file size (max 50MB for all media)
		if (file.size > 50 * 1024 * 1024) {
			return
		}

		setSelectedFile(file)

		// Create preview URL for image/video
		const mediaType = getMediaTypeFromFile(file)
		if (mediaType === 'image' || mediaType === 'video') {
			const url = URL.createObjectURL(file)
			setPreviewUrl(url)
		} else {
			setPreviewUrl(null)
		}
	}, [])

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (file) {
				handleFileSelect(file)
			}
		},
		[handleFileSelect],
	)

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		setIsDragging(true)
	}, [])

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		setIsDragging(false)
	}, [])

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			setIsDragging(false)

			const file = e.dataTransfer.files[0]
			if (file) {
				handleFileSelect(file)
			}
		},
		[handleFileSelect],
	)

	const handleSend = useCallback(() => {
		if (selectedFile && detectedMediaType) {
			onMediaSelect(selectedFile, detectedMediaType)
		}
	}, [selectedFile, detectedMediaType, onMediaSelect])

	const handleClear = useCallback(() => {
		setSelectedFile(null)
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl)
		}
		setPreviewUrl(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}, [previewUrl])

	const handleClose = useCallback(() => {
		handleClear()
		onClose()
	}, [handleClear, onClose])

	return (
		<div className="border-t bg-muted/30">
			<div className="flex items-center justify-between border-b px-4 py-2">
				<div className="flex items-center gap-2">
					<Paperclip className="h-4 w-4 text-primary" />
					<span className="text-sm font-medium">ส่งไฟล์แนบ</span>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6"
					onClick={handleClose}
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			<div className="p-4">
				<AnimatePresence mode="wait">
					{selectedFile ? (
						<motion.div
							key="preview"
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="space-y-3"
						>
							{/* Media Preview */}
							<div className="relative mx-auto max-w-xs overflow-hidden rounded-lg border bg-background">
								{detectedMediaType === 'image' && previewUrl && (
									<img
										src={previewUrl}
										alt="Preview"
										className="max-h-48 w-full object-contain"
									/>
								)}
								{detectedMediaType === 'video' && previewUrl && (
									<video
										src={previewUrl}
										className="max-h-48 w-full object-contain"
										controls
									/>
								)}
								{detectedMediaType === 'audio' && (
									<div className="flex flex-col items-center justify-center p-6">
										<Mic className="mb-2 h-12 w-12 text-muted-foreground" />
										<audio
											src={
												previewUrl ||
												(selectedFile
													? URL.createObjectURL(selectedFile)
													: undefined)
											}
											controls
											className="w-full"
										/>
									</div>
								)}
								{detectedMediaType === 'file' && (
									<div className="flex flex-col items-center justify-center p-6">
										<FileText className="mb-2 h-12 w-12 text-muted-foreground" />
										<p className="text-sm font-medium text-muted-foreground">
											ไฟล์แนบ
										</p>
									</div>
								)}
								<Button
									variant="ghost"
									size="icon"
									onClick={handleClear}
									disabled={isUploading}
									className="absolute right-2 top-2 h-6 w-6 rounded-full bg-black/50 text-white hover:bg-black/70 disabled:opacity-50"
								>
									<X className="h-4 w-4" />
								</Button>
							</div>

							{/* File info */}
							{selectedFile && detectedMediaType && (
								<div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
									{getMediaIcon(detectedMediaType)}
									<span className="font-medium">
										{getMediaLabel(detectedMediaType)}
									</span>
									<span>•</span>
									<span className="max-w-[200px] truncate">
										{selectedFile.name}
									</span>
									<span>({formatFileSize(selectedFile.size)})</span>
								</div>
							)}

							{/* Send button */}
							<div className="flex justify-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={handleClear}
									disabled={isUploading}
								>
									เปลี่ยนไฟล์
								</Button>
								<Button size="sm" onClick={handleSend} disabled={isUploading}>
									{isUploading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											กำลังอัพโหลด...
										</>
									) : (
										<>
											<Send className="mr-2 h-4 w-4" />
											ส่ง{detectedMediaType && getMediaLabel(detectedMediaType)}
										</>
									)}
								</Button>
							</div>
						</motion.div>
					) : (
						<motion.div
							key="upload"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							{/* Drop zone */}
							<div
								onClick={() => fileInputRef.current?.click()}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
								className={cn(
									'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
									isDragging
										? 'border-primary bg-primary/5'
										: 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
								)}
							>
								<Paperclip className="mb-2 h-10 w-10 text-muted-foreground" />
								<p className="text-sm font-medium">คลิกเพื่อเลือกไฟล์</p>
								<p className="mt-1 text-xs text-muted-foreground">
									หรือลากไฟล์มาวางที่นี่
								</p>
								<div className="mt-3 flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
									<span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
										<Image className="h-3 w-3" /> รูปภาพ
									</span>
									<span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
										<Video className="h-3 w-3" /> วิดีโอ
									</span>
									<span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
										<Mic className="h-3 w-3" /> เสียง
									</span>
									<span className="flex items-center gap-1 rounded-full bg-muted px-2 py-1">
										<FileText className="h-3 w-3" /> ไฟล์
									</span>
								</div>
								<p className="mt-2 text-xs text-muted-foreground">
									(สูงสุด 50MB)
								</p>
							</div>

							<input
								ref={fileInputRef}
								type="file"
								accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
								onChange={handleInputChange}
								className="hidden"
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	)
}
